import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";
import { SweetAlertShow } from "../master/Master";
import usePrivateLink from "../config/usePrivateLink";
import { fetchBusListWithLocation } from "../../api/Transport";
import { imgBaseUrl } from "../../api/api";

const LIVE_THRESHOLD_MIN = 5;
const RECENT_THRESHOLD_MIN = 30;

// "2026-08-27 15:29:29" -> parseable in every browser
const toDate = (dateTimeStr) =>
  dateTimeStr ? new Date(dateTimeStr.replace(" ", "T")) : null;

export default function BusListWithLocation() {
  const hasAccess = usePrivateLink();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [buses, setBuses] = useState([]);
  const [search, setSearch] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const pollRef = useRef(null);

  const getBusList = useCallback(async (silent = false) => {
    silent ? setRefreshing(true) : setLoading(true);
    try {
      const response = await fetchBusListWithLocation();
      if (response.data.status) {
        setBuses(response.data.data || []);
      } else if (!silent) {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      if (!silent) {
        SweetAlertShow(
          "error",
          "Error",
          error?.response?.data?.message ??
            error?.response?.message ??
            error?.message,
        );
      }
    } finally {
      silent ? setRefreshing(false) : setLoading(false);
    }
  }, []);

  useEffect(() => {
    getBusList();
  }, [getBusList]);

  useEffect(() => {
    if (autoRefresh) {
      pollRef.current = setInterval(() => getBusList(true), 300000);
    }
    return () => clearInterval(pollRef.current);
  }, [autoRefresh, getBusList]);

  // --- helpers ---------------------------------------------------------

  const getLatestLocation = (bus) => bus.bus_location || null;

  const minutesSince = (dateTimeStr) => {
    const d = toDate(dateTimeStr);
    if (!d) return null;
    return Math.floor((Date.now() - d.getTime()) / 60000);
  };

  const locationStatus = (bus) => {
    const loc = getLatestLocation(bus);
    const mins = minutesSince(loc?.date_time);
    if (mins === null)
      return {
        label: "No signal",
        tone: "secondary",
        icon: "ri-signal-wifi-off-line",
      };
    if (mins <= LIVE_THRESHOLD_MIN)
      return { label: "Live", tone: "success", icon: "ri-radio-button-line" };
    if (mins <= RECENT_THRESHOLD_MIN)
      return { label: `${mins}m ago`, tone: "warning", icon: "ri-time-line" };
    return {
      label: "Offline",
      tone: "danger",
      icon: "ri-signal-wifi-off-line",
    };
  };

  const filteredBuses = buses.filter((b) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      b.bus_number?.toLowerCase().includes(q) ||
      b.driver_name?.toLowerCase().includes(q) ||
      b.driver_number?.includes(q)
    );
  });

  const liveCount = buses.filter((b) => {
    const mins = minutesSince(getLatestLocation(b)?.date_time);
    return mins !== null && mins <= LIVE_THRESHOLD_MIN;
  }).length;
  const offlineCount = buses.filter((b) => {
    const mins = minutesSince(getLatestLocation(b)?.date_time);
    return mins === null || mins > RECENT_THRESHOLD_MIN;
  }).length;
  // API has no speed field, so "Moving" isn't derivable — track how many
  // buses have ever reported a location instead.
  const trackedCount = buses.filter((b) => !!getLatestLocation(b)).length;

  const StatCard = ({ icon, label, value, tone }) => (
    <div className="col-6 col-md-3">
      <div
        className="d-flex align-items-center gap-2 p-3 rounded-3 h-100"
        style={{ background: "#f8f9fb" }}
      >
        <div
          className={`d-flex align-items-center justify-content-center bg-label-${tone} rounded-3`}
          style={{ width: 38, height: 38, fontSize: 17, flexShrink: 0 }}
        >
          <i className={icon} />
        </div>
        <div>
          <div className="fw-bold" style={{ fontSize: 18, lineHeight: 1 }}>
            {loading ? <span className="placeholder col-4" /> : value}
          </div>
          <small className="text-muted">{label}</small>
        </div>
      </div>
    </div>
  );

  const SkeletonRow = ({ i }) => (
    <tr key={i}>
      {Array.from({ length: 7 }).map((__, j) => (
        <td key={j}>
          <span className="placeholder-glow d-block">
            <span
              className="placeholder col-8"
              style={{ height: 14, borderRadius: 4 }}
            />
          </span>
        </td>
      ))}
    </tr>
  );

  return (
    <>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid mt-2">
                <div className="row py-5">
                  <Master />
                  <div className="col-md-12">
                    <div className="card p-3 mb-3 shadow-sm border-0">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: "14px",
                              background:
                                "linear-gradient(135deg,#185fa5,#4f8cff)",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 22,
                              boxShadow: "0 4px 12px rgba(24,95,165,0.25)",
                            }}
                          >
                            <i className="ri-map-pin-line" />
                          </div>
                          <div>
                            <h5 className="m-0 fw-bold">Bus Live Tracking</h5>
                            <small className="text-muted">
                              Current location of every bus{" "}
                              {refreshing && (
                                <span className="ms-1">
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    style={{ width: 10, height: 10 }}
                                  />
                                </span>
                              )}
                            </small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div
                            className="input-group"
                            style={{ maxWidth: 240 }}
                          >
                            <span className="input-group-text bg-transparent border-end-0">
                              <i className="ri-search-line text-muted" />
                            </span>
                            <input
                              type="text"
                              className="form-control border-start-0"
                              placeholder="Search bus, driver, phone…"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                            />
                          </div>
                          <button
                            type="button"
                            className={`btn btn-sm rounded-3 ${autoRefresh ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => setAutoRefresh((v) => !v)}
                            title="Toggle auto-refresh"
                          >
                            <i className="ri-refresh-line me-1" />
                            {autoRefresh ? "Live" : "Paused"}
                          </button>
                        </div>
                      </div>

                      <div className="row g-2">
                        <StatCard
                          icon="ri-bus-2-line"
                          label="Total buses"
                          value={buses.length}
                          tone="primary"
                        />
                        <StatCard
                          icon="ri-radio-button-line"
                          label="Live now"
                          value={liveCount}
                          tone="success"
                        />
                        <StatCard
                          icon="ri-map-pin-2-line"
                          label="Ever tracked"
                          value={trackedCount}
                          tone="info"
                        />
                        <StatCard
                          icon="ri-signal-wifi-off-line"
                          label="Offline"
                          value={offlineCount}
                          tone={offlineCount ? "danger" : "secondary"}
                        />
                      </div>
                    </div>

                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead>
                              <tr style={{ background: "#f8f9fb" }}>
                                <th className="ps-3">#</th>
                                <th>
                                  <i className="ri-bus-2-line me-1 text-muted" />
                                  Bus
                                </th>
                                {/* <th>
                                  <i className="ri-user-3-line me-1 text-muted" />
                                  Driver
                                </th> */}
                                <th>
                                  <i className="ri-map-pin-2-line me-1 text-muted" />
                                  Last Location
                                </th>
                                <th>
                                  <i className="ri-time-line me-1 text-muted" />
                                  Last Update
                                </th>
                                <th>
                                  <i className="ri-signal-tower-line me-1 text-muted" />
                                  Status
                                </th>
                                <th className="text-end pe-3">
                                  <i className="ri-settings-3-line me-1 text-muted" />
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                Array.from({ length: 6 }).map((_, i) => (
                                  <SkeletonRow key={i} i={i} />
                                ))
                              ) : filteredBuses.length === 0 ? (
                                <tr>
                                  <td colSpan={7} className="text-center py-5">
                                    <div className="d-flex flex-column align-items-center text-muted">
                                      <i
                                        className="ri-map-pin-line"
                                        style={{ fontSize: 48, opacity: 0.25 }}
                                      />
                                      <div className="mt-2 fw-semibold">
                                        {search
                                          ? "No buses match your search"
                                          : "No buses found"}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                filteredBuses.map((bus, index) => {
                                  const loc = getLatestLocation(bus);
                                  const status = locationStatus(bus);
                                  const lat = loc
                                    ? parseFloat(loc.latitude)
                                    : null;
                                  const lng = loc
                                    ? parseFloat(loc.longitude)
                                    : null;
                                  return (
                                    <tr
                                      key={bus.id}
                                      role="button"
                                      onClick={() =>
                                        hasAccess("/bus/live/tracking") &&
                                        navigate(
                                          `/bus/live/tracking/${bus.id}`,
                                          {
                                            state: { bus },
                                          },
                                        )
                                      }
                                      style={{ cursor: "pointer" }}
                                    >
                                      <td className="ps-3 text-muted">
                                        {index + 1}
                                      </td>
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          {bus.bus_image ? (
                                            <img
                                              src={imgBaseUrl + bus.bus_image}
                                              alt={bus.bus_number}
                                              style={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: "10px",
                                                objectFit: "cover",
                                                flexShrink: 0,
                                              }}
                                            />
                                          ) : (
                                            <div
                                              className="rounded-3 d-flex align-items-center justify-content-center bg-label-primary"
                                              style={{
                                                width: 38,
                                                height: 38,
                                                flexShrink: 0,
                                              }}
                                            >
                                              <i className="ri-bus-2-fill" />
                                            </div>
                                          )}
                                          <span className="fw-medium">
                                            {bus.bus_number}
                                          </span>
                                        </div>
                                      </td>
                                      {/* <td>
                                        <div className="d-flex align-items-center gap-2">
                                          <i className="ri-account-circle-line text-muted" />
                                          {bus.driver_name}
                                        </div>
                                      </td> */}
                                      <td>
                                        {loc ? (
                                          <span
                                            className="text-muted"
                                            style={{ fontSize: 13 }}
                                          >
                                            <i className="ri-map-pin-2-fill text-primary me-1" />
                                            {lat?.toFixed(5)}, {lng?.toFixed(5)}
                                          </span>
                                        ) : (
                                          <span className="text-muted">—</span>
                                        )}
                                      </td>
                                      <td>
                                        {loc?.date_time ? (
                                          <small className="text-muted">
                                            {toDate(
                                              loc.date_time,
                                            )?.toLocaleTimeString([], {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </small>
                                        ) : (
                                          <span className="text-muted">—</span>
                                        )}
                                      </td>
                                      <td>
                                        <span
                                          className={`badge bg-label-${status.tone}`}
                                        >
                                          <i
                                            className={`${status.icon} me-1`}
                                          />
                                          {status.label}
                                        </span>
                                      </td>
                                      <td className="pe-3">
                                        <div className="d-flex justify-content-end">
                                          <button
                                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate(
                                                `/bus/live/tracking/${bus.id}`,
                                                {
                                                  state: { bus },
                                                },
                                              );
                                            }}
                                          >
                                            <i className="ri-map-2-line" /> View
                                            on Map
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
